// Einstellungen: Ausgänge, Metadaten, Stream-Monitor, Klang, Stille, Sender.

import { useEffect, useRef, useState } from "react";
import { api, fmtTime, toast, useLoad } from "../api";
import { Playback } from "./content/Content";
import { OwnPanel } from "./sound/OwnPanel";
import { Empty, ErrorBanner, Field, HowTo, Meter, Modal, PageHead, StateBadge, Switch, Tip, useInstance, useMe, useStatus } from "../ui";

const TABS: [string, string][] = [
  ["ausgaenge", "Ausgänge"],
  ["metadaten", "Metadaten"],
  ["monitor", "Stream-Monitor"],
  ["klang", "Klang"],
  ["stille", "Stille"],
  ["abspielen", "Abspielen"],
  ["sender", "Sender"],
];

function saved(r: any, what: string) {
  toast(r?.reloaded === false ? `${what} gespeichert – Engine nicht erreichbar, gilt beim nächsten Start` : `${what} gespeichert und übernommen`);
}

// ---------------------------------------------------------------- Ausgänge

const NEW_OUTPUT = {
  name: "", enabled: true, protocol: "icecast", host: "", port: 8000, mount: "/", username: "source", format: "mp3",
  bitrate: 128, channels: 2, samplerate: 44100, stream_name: "", genre: "", description: "", url: "", public: false,
  sid: null, send_metadata: true, listen_url: "", meta_kinds: ["music", "live"],
};

function PasswordField({ row, value, onChange, envName }: { row: any; value: any; onChange: (patch: any) => void; envName?: string }) {
  const fromEnv = value.password === `env:${envName}` || (value.password === undefined && row?.password_from_env);
  return (
    <Field
      label="Passwort"
      hint={row?.password_set && !value.password ? "Gesetzt. Leer lassen, um es zu behalten." : "Wird verschlüsselt übertragen und nie wieder angezeigt."}
    >
      <input
        type="password"
        autoComplete="new-password"
        disabled={fromEnv}
        placeholder={fromEnv ? "aus der Server-Konfiguration" : row?.password_set ? "•••••••• (gesetzt)" : ""}
        value={fromEnv ? "" : value.password ?? ""}
        onChange={(e) => onChange({ password: e.target.value, clear_password: false })}
      />
      {envName && (
        <label className="row small" style={{ marginTop: 4 }}>
          <input type="checkbox" checked={!!fromEnv} onChange={(e) => onChange({ password: e.target.checked ? `env:${envName}` : "", clear_password: !e.target.checked && row?.password_from_env })} />
          Quellpasswort des eigenen Icecast verwenden (aus der Server-Konfiguration)
        </label>
      )}
    </Field>
  );
}

function OutputEditor({ row, onClose }: { row: any | null; onClose: (changed: boolean) => void }) {
  const [v, setV] = useState<any>(row ? { ...row, password: undefined, stats_password: undefined } : { ...NEW_OUTPUT });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: any) => setV((x: any) => ({ ...x, ...patch }));
  const save = async () => {
    try {
      const body = { ...v };
      delete body.id; delete body.station_id; delete body.created_at; delete body.updated_at; delete body.password_set; delete body.password_from_env; delete body.stats_password_set; delete body.window_active; delete body.next_start;
      if (body.password === undefined) delete body.password;
      if (!body.stats_password) delete body.stats_password;
      const r = row ? await api(`/api/outputs/${row.id}`, { method: "PUT", body }) : await api("/api/outputs", { body });
      saved(r, "Ausgang");
      onClose(true);
    } catch (e: any) {
      setError(e.message);
    }
  };
  return (
    <Modal title={row ? `Ausgang „${row.name}“` : "Neuer Ausgang"} onClose={() => onClose(false)}>
      <ErrorBanner error={error} />
      <div className="form">
        <Field label="Name"><input type="text" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="z. B. Hauptstream MP3" /></Field>
        <Field label="Aktiv"><Switch checked={v.enabled} onChange={(x) => set({ enabled: x })} label={v.enabled ? "sendet" : "aus"} /></Field>
        <Field label="Server-Art">
          <select value={v.protocol} onChange={(e) => set({ protocol: e.target.value, port: e.target.value === "shoutcast" && v.port === 8000 ? 8000 : v.port })}>
            <option value="icecast">Icecast 2</option>
            <option value="shoutcast">Shoutcast (v1 / v2)</option>
          </select>
        </Field>
        <Field label="Host" hint="Servername oder IP, ohne http://"><input type="text" value={v.host} onChange={(e) => set({ host: e.target.value.trim() })} /></Field>
        <Field label="Port"><input type="number" value={v.port} onChange={(e) => set({ port: Number(e.target.value) })} /></Field>
        {v.protocol === "icecast" ? (
          <Field label="Mount" hint="z. B. /live"><input type="text" value={v.mount} onChange={(e) => set({ mount: e.target.value.trim() })} /></Field>
        ) : (
          <Field label="Stream-ID (nur Shoutcast v2)" hint="leer bei Shoutcast v1"><input type="number" value={v.sid ?? ""} onChange={(e) => set({ sid: e.target.value ? Number(e.target.value) : null })} /></Field>
        )}
        {v.protocol === "icecast" && <Field label="Benutzer"><input type="text" value={v.username} onChange={(e) => set({ username: e.target.value })} /></Field>}
        <PasswordField row={row} value={v} onChange={set} envName={v.protocol === "icecast" ? "ICECAST_SOURCE_PASSWORD" : undefined} />
        <Field label="Format">
          <select value={v.format} onChange={(e) => set({ format: e.target.value })}>
            <option value="mp3">MP3</option>
            <option value="aac">AAC (AAC-LC)</option>
          </select>
        </Field>
        <Field label="Bitrate">
          <select value={v.bitrate} onChange={(e) => set({ bitrate: Number(e.target.value) })}>
            {[32, 48, 64, 96, 112, 128, 160, 192, 256, 320].map((b) => <option key={b} value={b}>{b} kbit/s</option>)}
          </select>
        </Field>
        <Field label="Kanäle">
          <select value={v.channels} onChange={(e) => set({ channels: Number(e.target.value) })}>
            <option value={2}>Stereo</option>
            <option value={1}>Mono</option>
          </select>
        </Field>
        <Field label="Samplerate">
          <select value={v.samplerate} onChange={(e) => set({ samplerate: Number(e.target.value) })}>
            {[22050, 32000, 44100, 48000].map((r) => <option key={r} value={r}>{(r / 1000).toLocaleString("de-DE")} kHz</option>)}
          </select>
        </Field>
        <Field label="Stream-Name"><input type="text" value={v.stream_name} onChange={(e) => set({ stream_name: e.target.value })} /></Field>
        <Field label="Genre"><input type="text" value={v.genre} onChange={(e) => set({ genre: e.target.value })} /></Field>
        <Field label="Beschreibung" full><input type="text" value={v.description} onChange={(e) => set({ description: e.target.value })} /></Field>
        <Field label="Website"><input type="text" value={v.url} onChange={(e) => set({ url: e.target.value })} /></Field>
        <Field label="Hörer-Link (optional)" hint="Wird unter Streams angezeigt"><input type="text" value={v.listen_url} onChange={(e) => set({ listen_url: e.target.value })} /></Field>
        <Field label="Sendezeiten des Streams" full hint="Leer = immer an. Sonst sendet der Stream nur in diesen Fenstern; außerhalb ist er getrennt und der Mount frei (z. B. Morningshow Mo–Fr 06:00–10:00).">
          <OutputWindows value={v.windows ?? []} onChange={(w) => set({ windows: w })} />
        </Field>
        <Field label="Statistik-Login (optional)" hint="Admin-Zugang des Stream-Servers – damit zählt Radiocast Hörer genauer (Länder, Hördauer). Ohne zählt es nur die Summe.">
          <input type="text" autoComplete="off" placeholder="Benutzer, z. B. admin" value={v.stats_user ?? ""} onChange={(e) => set({ stats_user: e.target.value })} />
        </Field>
        <Field label="Statistik-Passwort" hint={row?.stats_password_set ? "Gesetzt. Leer lassen, um es zu behalten." : "Wird nie wieder angezeigt."}>
          <input type="password" autoComplete="new-password" placeholder={row?.stats_password_set ? "•••••••• (gesetzt)" : ""} value={v.stats_password ?? ""} onChange={(e) => set({ stats_password: e.target.value })} />
        </Field>
        <Field label="Metadaten senden"><Switch checked={v.send_metadata} onChange={(x) => set({ send_metadata: x })} label="Titel an diesen Server senden" /></Field>
        {v.send_metadata && (
          <div className="field full">
            <span>Titel senden bei (Art des Elements)</span>
            <small>Wie bei mAirList: maßgeblich ist die Art, nicht Kategorie oder Ordner. Bei allen anderen bleibt der letzte Titel stehen (oder der feste Text aus „Metadaten“). Beispiel laut.fm: START_AD_BREAK als Art „Musik“ anlegen – dann löst er dort die Werbung aus.</small>
            <div className="row wrap" style={{ marginTop: 6, gap: 4 }}>
              {META_KINDS.map(([k, l]) => {
                const on = (v.meta_kinds ?? []).includes(k);
                return <label key={k} className="badge" style={{ cursor: "pointer", background: on ? "var(--accent-2)" : undefined }}><input type="checkbox" checked={on} onChange={() => set({ meta_kinds: on ? v.meta_kinds.filter((x: string) => x !== k) : [...(v.meta_kinds ?? []), k] })} /> {l}</label>;
              })}
            </div>
          </div>
        )}
        <Field label="Verzeichnis"><Switch checked={v.public} onChange={(x) => set({ public: x })} label="In öffentlichen Verzeichnissen listen" /></Field>
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <span className="spacer" />
        <button className="btn" onClick={() => onClose(false)}>Abbrechen</button>
        <button className="btn primary" onClick={save}>Speichern</button>
      </div>
    </Modal>
  );
}

function Outputs() {
  const { data, error, reload } = useLoad<any[]>("/api/outputs");
  const status = useStatus();
  const me = useMe();
  const [edit, setEdit] = useState<any | null | undefined>(undefined);
  const live = (id: string) => (status?.engine?.outputs ?? []).find((o: any) => o.id === id);
  const toggle = async (o: any) => {
    try {
      saved(await api(`/api/outputs/${o.id}`, { method: "PUT", body: { enabled: !o.enabled } }), "Ausgang");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const remove = async (o: any) => {
    if (!window.confirm(`Ausgang „${o.name}“ löschen? Die Verbindung wird sofort getrennt.`)) return;
    await api(`/api/outputs/${o.id}`, { method: "DELETE" });
    toast("Ausgang gelöscht");
    reload();
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Ausgänge</h2>
        <span className="spacer" />
        {me?.role === "admin" && <button className="btn primary" onClick={() => setEdit(null)}>+ Ausgang</button>}
      </div>
      <Tip>Jeder Ausgang ist ein eigener Encoder: MP3 und AAC, verschiedene Bitraten und mehrere Server gleichzeitig. Änderungen gelten sofort; unveränderte Ausgänge bleiben verbunden.</Tip>
      <ErrorBanner error={error} />
      {!data?.length ? <Empty>Noch kein Ausgang. Lege das erste Ziel an – Zugangsdaten gibst du nur hier ein.</Empty> : (
        <table className="list">
          <thead><tr><th>Name</th><th>Ziel</th><th>Format</th><th>Zustand</th><th></th></tr></thead>
          <tbody>
            {data.map((o) => {
              const s = live(o.id);
              return (
                <tr key={o.id}>
                  <td><b>{o.name}</b>{!o.password_set && o.enabled && <div className="small" style={{ color: "var(--warn)" }}>Kein Passwort gesetzt</div>}
                    {(o.windows ?? []).length > 0 && <div className="small" style={{ color: o.window_active ? "var(--ok)" : "var(--ink-3, #777)" }}>{o.window_active ? "● im Zeitfenster" : `wartet${o.next_start ? ` bis ${new Date(o.next_start).toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" })}` : ""}`}</div>}</td>
                  <td className="mono">{o.protocol === "icecast" ? "Icecast" : "Shoutcast"} · {o.host}:{o.port}{o.protocol === "icecast" ? o.mount : o.sid ? ` #${o.sid}` : ""}</td>
                  <td className="small">{o.format.toUpperCase()} {o.bitrate} · {o.channels === 1 ? "Mono" : "Stereo"} · {o.samplerate / 1000} kHz</td>
                  <td>{o.enabled ? <StateBadge state={s?.state ?? "verbinde"} /> : <span className="badge">aus</span>}{s?.error && <div className="small muted">{s.error}</div>}</td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    {me?.role === "admin" && (
                      <>
                        <Switch checked={o.enabled} onChange={() => toggle(o)} />{" "}
                        <button className="btn small" onClick={() => setEdit(o)}>Bearbeiten</button>{" "}
                        <button className="btn small danger" onClick={() => remove(o)}>Löschen</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {edit !== undefined && <OutputEditor row={edit} onClose={(changed) => { setEdit(undefined); if (changed) reload(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- Metadaten

export const META_KINDS: [string, string][] = [["music", "Musik"], ["jingle", "Jingle"], ["dropper", "Dropper"], ["station_id", "Station-ID"], ["imaging", "Sweeper/Imaging"], ["ad", "Werbung"], ["news_bed", "Nachrichten"], ["spoken", "Wort"], ["voicetrack", "Voicetrack"], ["other", "Stille/Sonstiges"], ["live", "Live/Stream-Monitor"]];

const PLACEHOLDERS = ["{interpret}", "{titel}", "{sender}", "{kategorie}", "{album}", "{jahr}"];

function preview(t: string, station = "Sender") {
  const s = t
    .replace(/\{(interpret|artist)\}/g, "Beispielband")
    .replace(/\{(titel|title)\}/g, "Sommerlied")
    .replace(/\{(sender|station)\}/g, station)
    .replace(/\{(kategorie|category)\}/g, "Hits")
    .replace(/\{album\}/g, "")
    .replace(/\{(jahr|year)\}/g, "2024")
    .replace(/\(\)/g, "")
    .replace(/\s{2,}/g, " ");
  return s.trim().replace(/^[-–|·\s]+|[-–|·\s]+$/g, "");
}

function Metadata() {
  const { data, reload } = useLoad("/api/metadata");
  const inst = useInstance();
  const status = useStatus();
  const [v, setV] = useState<any>(null);
  const tpl = useRef<HTMLInputElement>(null);
  useEffect(() => { if (data) setV(data); }, [data]);
  if (!v) return null;
  const dirty = JSON.stringify(v) !== JSON.stringify(data);
  const insert = (p: string) => {
    const el = tpl.current;
    const at = el?.selectionStart ?? v.template.length;
    setV({ ...v, template: v.template.slice(0, at) + p + v.template.slice(at) });
  };
  const save = async () => {
    try {
      // The old choice by category is replaced by the kinds per output: cleared on save.
      saved(await api("/api/metadata", { method: "PUT", body: { ...v, categories: [] } }), "Metadaten");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="card">
      <h2>Metadaten</h2>
      <p className="muted" style={{ marginTop: 0 }}>Der Text, den Hörer im Player sehen. Er geht an alle Ausgänge, bei denen „Metadaten senden“ an ist.</p>
      <div className="form">
        <Field label="Vorlage" full hint={<>Vorschau: <b>{preview(v.template, inst?.name) || "–"}</b></>}>
          <input ref={tpl} type="text" value={v.template} onChange={(e) => setV({ ...v, template: e.target.value })} />
          <div className="row wrap" style={{ marginTop: 4 }}>
            {PLACEHOLDERS.map((p) => <button key={p} className="btn small" onClick={() => insert(p)} type="button">{p}</button>)}
          </div>
        </Field>
        <div className="field full">
          <span>Welche Elemente senden ihren Titel?</span>
          <small>Das legst du je Ausgang nach der Art des Elements fest (Musik, Jingle, Dropper …) – unter <a href="#/einstellungen/ausgaenge">Ausgänge</a>, beim jeweiligen Encoder. Container senden die Titel ihrer Bausteine einzeln, jeweils beim Start des Bausteins.</small>
        </div>
        <Field label="Bei allem anderen">
          <select value={v.otherwise} onChange={(e) => setV({ ...v, otherwise: e.target.value })}>
            <option value="last_music">letzten gesendeten Titel stehen lassen</option>
            <option value="fixed">festen Text zeigen</option>
          </select>
        </Field>
        <Field label="Fester Text" hint="leer = Sendername">
          <input type="text" disabled={v.otherwise !== "fixed"} value={v.fixed_text} onChange={(e) => setV({ ...v, fixed_text: e.target.value })} />
        </Field>
        <Field label="Während ein überwachter Stream on air ist" full hint={<>Platzhalter {"{name}"} (Name des Streams) und {"{sender}"}</>}>
          <input type="text" value={v.live_template} onChange={(e) => setV({ ...v, live_template: e.target.value })} />
        </Field>
      </div>
      <div className="small muted" style={{ marginTop: 10 }}>Gerade gesendet: <b>{status?.engine?.metadata ?? "–"}</b></div>
      <div className="savebar">
        <span className="small muted">{dirty ? "Ungespeicherte Änderungen" : "Alle Änderungen gespeichert"}</span>
        <span className="spacer" />
        <button className="btn" disabled={!dirty} onClick={() => setV(data)}>Verwerfen</button>
        <button className="btn primary" disabled={!dirty} onClick={save}>Speichern</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Stream-Monitor

const NEW_MONITOR = { name: "", enabled: true, url: "http://", username: "", priority: 1, fade_in_ms: 1500, fade_out_ms: 2000, threshold_db: -45, activate_ms: 1500, hold_ms: 8000, pause_automation: false, pass_title: true, title_template: "" };

function MonitorEditor({ row, onClose }: { row: any | null; onClose: (changed: boolean) => void }) {
  const [v, setV] = useState<any>(row ? { ...row, password: undefined } : { ...NEW_MONITOR });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: any) => setV((x: any) => ({ ...x, ...patch }));
  const save = async () => {
    try {
      const body = { ...v };
      for (const k of ["id", "station_id", "created_at", "updated_at", "password_set", "password_from_env"]) delete body[k];
      if (body.password === undefined) delete body.password;
      const r = row ? await api(`/api/monitors/${row.id}`, { method: "PUT", body }) : await api("/api/monitors", { body });
      saved(r, "Stream");
      onClose(true);
    } catch (e: any) {
      setError(e.message);
    }
  };
  return (
    <Modal title={row ? `Stream „${row.name}“` : "Stream überwachen"} onClose={() => onClose(false)}>
      <ErrorBanner error={error} />
      <div className="form">
        <Field label="Name" hint="erscheint in Anzeige, Metadaten und Telegram"><input type="text" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="z. B. Studio Geretsried" /></Field>
        <Field label="Aktiv"><Switch checked={v.enabled} onChange={(x) => set({ enabled: x })} label={v.enabled ? "wird überwacht" : "aus"} /></Field>
        <Field label="Stream-Adresse mit Mount" full hint="z. B. http://server:8000/live – MP3, AAC oder Ogg"><input type="url" value={v.url} onChange={(e) => set({ url: e.target.value.trim() })} /></Field>
        <Field label="Benutzer (optional)"><input type="text" value={v.username} onChange={(e) => set({ username: e.target.value })} /></Field>
        <PasswordField row={row} value={v} onChange={set} />
        <Field label="Priorität" hint="1 = höchste; senden mehrere, gewinnt die kleinste Zahl"><input type="number" min={1} max={99} value={v.priority} onChange={(e) => set({ priority: Number(e.target.value) })} /></Field>
        <Field label="Mindestpegel (dBFS)" hint="darunter gilt der Stream als stumm"><input type="number" step={1} min={-90} max={0} value={v.threshold_db} onChange={(e) => set({ threshold_db: Number(e.target.value) })} /></Field>
        <Field label="Einblendzeit (ms)"><input type="number" step={100} min={0} value={v.fade_in_ms} onChange={(e) => set({ fade_in_ms: Number(e.target.value) })} /></Field>
        <Field label="Ausblendzeit (ms)"><input type="number" step={100} min={0} value={v.fade_out_ms} onChange={(e) => set({ fade_out_ms: Number(e.target.value) })} /></Field>
        <Field label="Übernahme nach (ms)" hint="so lange muss der Pegel stehen"><input type="number" step={100} min={0} value={v.activate_ms} onChange={(e) => set({ activate_ms: Number(e.target.value) })} /></Field>
        <Field label="Rückgabe nach (ms)" hint="so lange darf er stumm sein oder weg"><input type="number" step={500} min={0} value={v.hold_ms} onChange={(e) => set({ hold_ms: Number(e.target.value) })} /></Field>
        <Field label="Automation während der Übernahme" full>
          <Switch checked={v.pause_automation} onChange={(x) => set({ pause_automation: x })} label={v.pause_automation ? "pausiert und macht danach an derselben Stelle weiter" : "läuft unhörbar weiter"} />
        </Field>
        <Field label="Titel des Streams durchreichen" full hint="Während der Übernahme gehen die Titel des eingehenden Streams (StreamTitle) an die Ausgänge mit der Art „Live/Stream-Monitor“. Sendet der Stream keinen Titel, geht der Name dieses Eintrags raus.">
          <Switch checked={v.pass_title !== false} onChange={(x) => set({ pass_title: x })} label={v.pass_title !== false ? "an" : "aus – Text aus Einstellungen › Metadaten (live)"} />
        </Field>
        {v.pass_title !== false && (
          <Field label="Vorlage (optional)" full hint={<>leer = nur der Titel. Platzhalter {"{titel}"} (Titel des Streams), {"{name}"} (dieser Eintrag), {"{sender}"} – z. B. „{"{name}"} – {"{titel}"}“</>}>
            <input type="text" value={v.title_template ?? ""} onChange={(e) => set({ title_template: e.target.value })} placeholder="{titel}" />
          </Field>
        )}
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <span className="spacer" />
        <button className="btn" onClick={() => onClose(false)}>Abbrechen</button>
        <button className="btn primary" onClick={save}>Speichern</button>
      </div>
    </Modal>
  );
}

function Monitors() {
  const { data, error, reload } = useLoad<any[]>("/api/monitors");
  const status = useStatus();
  const me = useMe();
  const [edit, setEdit] = useState<any | null | undefined>(undefined);
  const live = (id: string) => (status?.engine?.monitors ?? []).find((m: any) => m.id === id);
  const remove = async (m: any) => {
    if (!window.confirm(`Überwachung „${m.name}“ löschen?`)) return;
    await api(`/api/monitors/${m.id}`, { method: "DELETE" });
    reload();
  };
  const onAir = status?.engine?.on_air;
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Stream-Monitor</h2>
        <span className="spacer" />
        {me?.role === "admin" && <button className="btn primary" onClick={() => setEdit(null)}>+ Stream</button>}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Sendet ein eingetragener Stream (verbunden und lauter als der Mindestpegel), geht er über das Programm on air. Fällt er weg, übernimmt wieder die Automation. Jede Übernahme meldet Telegram.
      </p>
      <div className="banner ok" style={{ background: onAir?.kind === "stream" ? "var(--bad-2)" : undefined, color: onAir?.kind === "stream" ? "var(--live)" : undefined }}>
        On air: <b>{onAir?.name ?? "–"}</b>
      </div>
      <ErrorBanner error={error} />
      {!data?.length ? <Empty>Noch kein Stream eingetragen.</Empty> : (
        <table className="list">
          <thead><tr><th>Prio</th><th>Name</th><th>Zustand</th><th style={{ width: 180 }}>Pegel</th><th></th></tr></thead>
          <tbody>
            {data.map((m) => {
              const s = live(m.id);
              return (
                <tr key={m.id}>
                  <td>{m.priority}</td>
                  <td><b>{m.name}</b> {s?.on_air && <span className="badge live">ON AIR</span>}<div className="mono muted">{m.url}</div></td>
                  <td>{m.enabled ? <StateBadge state={s?.state ?? "verbinde"} /> : <span className="badge">aus</span>}{s?.error && s.state !== "verbunden" && <div className="small muted">{s.error}</div>}</td>
                  <td><Meter db={s?.level_db} mark={m.threshold_db} /><div className="small muted">{s?.sending ? "sendet" : s?.state === "verbunden" ? "unter Mindestpegel" : s?.state === "fehler" ? (s?.error ?? "nicht erreichbar") : ""}{s?.title ? ` · „${s.title}“` : ""}{s?.resolved_url ? ` · Playlist → ${s.resolved_url}` : ""}</div></td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    {me?.role === "admin" && (
                      <>
                        <button className="btn small" onClick={() => setEdit(m)}>Bearbeiten</button>{" "}
                        <button className="btn small danger" onClick={() => remove(m)}>Löschen</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {edit !== undefined && <MonitorEditor row={edit} onClose={(changed) => { setEdit(undefined); if (changed) reload(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- Klang

/** StereoTool's own web interface lives on its own port (it expects to sit at "/"). */
const STEREOTOOL_URL = `${window.location.protocol}//${window.location.hostname}:8443/`;

function Sound() {
  const { data: p, reload } = useLoad("/api/processor");
  const { data: presets, reload: reloadPresets } = useLoad<any[]>("/api/processor/presets");
  const { data: lspPresets } = useLoad<any[]>("/api/processor/lsp-presets");
  const me = useMe();
  const [license, setLicense] = useState("");
  const [showWeb, setShowWeb] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  if (!p) return null;
  const put = async (body: any, what = "Klang") => {
    try {
      saved(await api("/api/processor", { method: "PUT", body }), what);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const upload = async (f: File, replace = false) => {
    try {
      await api(`/api/processor/presets?name=${encodeURIComponent(f.name)}${replace ? "&replace=1" : ""}`, { raw: f });
      toast(`Preset „${f.name}“ hochgeladen`);
      reloadPresets();
    } catch (e: any) {
      if (e.status === 409 && window.confirm(`„${f.name}“ gibt es schon. Ersetzen?`)) return upload(f, true);
      toast(e.message, true);
    }
  };
  const activate = async (name: string) => {
    const r = await api(`/api/processor/presets/${encodeURIComponent(name)}/activate`, { method: "POST" });
    toast(r.applied ? `Preset „${name}“ ist aktiv` : `Preset „${name}“ gespeichert – gilt beim nächsten Start`);
    reloadPresets();
    reload();
  };
  const remove = async (name: string) => {
    if (!window.confirm(`Preset „${name}“ löschen?`)) return;
    try {
      await api(`/api/processor/presets/${encodeURIComponent(name)}`, { method: "DELETE" });
      reloadPresets();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const e = p.engine;
  const admin = me?.role === "admin";
  const choice = !p.enabled ? "off" : p.mode === "own" ? "own" : p.mode === "lsp" ? "lsp" : "stereotool";
  const selectedLspPreset = lspPresets?.find((x: any) => x.id === p.preset);
  const lspGroups = (lspPresets ?? []).reduce((groups: Record<string, any[]>, item: any) => {
    (groups[item.source] ??= []).push(item);
    return groups;
  }, {});
  const chooser = (
    <div className="card">
      <h2>Klangbearbeitung</h2>
      <div className="row wrap">
        {[["off", "Aus (nur Sicherheitsbegrenzer)"], ["own", "Eigene Klangbearbeitung"], ["stereotool", "StereoTool"], ["lsp", "LSP Plugins"]].map(([k, l]) => (
          <label key={k} className="badge" style={{ cursor: admin ? "pointer" : "default", padding: "6px 12px", background: choice === k ? "var(--accent-2)" : undefined, color: choice === k ? "var(--accent)" : undefined }}>
            <input type="radio" name="proc" disabled={!admin} checked={choice === k} onChange={() => put(k === "off" ? { enabled: false } : k === "own" ? { enabled: true, mode: "own" } : k === "lsp" ? { enabled: true, mode: "lsp", preset: p.preset || "balanced" } : { enabled: true, mode: p.mode === "own" || p.mode === "lsp" ? (p.library_installed ? "library" : "pipe") : p.mode })} /> {l}
          </label>
        ))}
      </div>
      <p className="small muted" style={{ marginBottom: 0 }}>Umschalten wirkt sofort im Sendeweg. Die Einstellungen beider Varianten bleiben erhalten.</p>
    </div>
  );
  if (choice === "own") return <div className="stack">{chooser}<OwnPanel own={p.own} onSaved={reload} /></div>;
  if (choice === "lsp") return <div className="stack">{chooser}<div className="card">
    <div className="row" style={{ marginBottom: 10 }}><h2 style={{ margin: 0 }}>LSP Stereo Compressor</h2><span className="spacer" />{e ? <StateBadge state={e.state} /> : <span className="badge">Engine nicht erreichbar</span>}</div>
    {e?.error && <div className="banner warn">⚠ {e.error} – der Sendeweg läuft solange über den Sicherheitsbegrenzer.</div>}
    {!p.lsp_installed && <div className="banner warn">Das LSP LV2-Paket ist auf diesem Server noch nicht installiert.</div>}
    <div className="form">
      <Field label="Preset" hint="Der Sicherheitsbegrenzer am Ende bleibt immer aktiv.">
        <select disabled={!admin || !p.lsp_installed} value={p.preset || "balanced"} onChange={(ev) => put({ enabled: true, mode: "lsp", preset: ev.target.value })}>
          {Object.entries(lspGroups).map(([source, options]) => (
            <optgroup key={source} label={source}>
              {options.map((option: any) => <option key={option.id} value={option.id}>{option.name}{option.source !== "Radiocast" ? " · LSP-Annäherung" : ""}</option>)}
            </optgroup>
          ))}
        </select>
      </Field>
      {selectedLspPreset && <p className="small muted">{selectedLspPreset.notes}</p>}
      <p className="small muted">Die mitgelieferten AMX-, StereoTool- und SoundSolution-Profile erscheinen als klangliche Annäherungen. Der LSP-Stereo-Kompressor bildet deren Mehrband-EQ, Stereo-Verbreiterung und Clipper nicht nach; der Sicherheitsbegrenzer bleibt aktiv.</p>
    </div>
  </div></div>;
  return (
    <div className="stack">
      {chooser}
      <div className="card">
        <div className="row" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>StereoTool</h2>
          <span className="spacer" />
          {e ? <StateBadge state={e.state} /> : <span className="badge">Engine nicht erreichbar</span>}
        </div>
        {e?.error && <div className="banner warn">⚠ {e.error} – der Sendeweg läuft solange über den Sicherheitsbegrenzer.</div>}
        <div className="form">
          <Field label="Klangbearbeitung">
            <Switch checked={p.enabled} onChange={(x) => admin && put({ enabled: x })} label={p.enabled ? "StereoTool an" : "aus (nur Sicherheitsbegrenzer)"} />
          </Field>
          <Field label="Betriebsart" hint={!p.library_installed ? "Bibliothek fehlt noch auf dem Server" : p.library_probe ? (p.library_probe.state === "läuft" ? `Bibliothek beim letzten Update getestet: läuft (Version ${p.library_probe.version ?? "?"}) – Umstellen ist sicher` : `Bibliothek beim letzten Update getestet: ${p.library_probe.error ?? "Fehler"} – bitte beim Programm bleiben`) : p.cmd_installed ? "Programm installiert" : "Programm fehlt"}>
            <select value={p.mode} disabled={!admin} onChange={(ev) => put({ mode: ev.target.value })}>
              <option value="library">In der Engine (Bibliothek) – Presetwechsel ohne Unterbrechung</option>
              <option value="pipe">Eigener Prozess (Programm) – Zwischenlösung</option>
            </select>
          </Field>
          <Field label="Lizenzschlüssel" hint={p.license_set ? "Eigener Schlüssel gesetzt." : p.license_from_env ? "Schlüssel aus der Server-Konfiguration wird verwendet." : "Ohne Schlüssel läuft StereoTool mit Hinweistönen."}>
            <div className="row">
              <input type="password" autoComplete="off" value={license} placeholder={p.license_set ? "•••••••• (gesetzt)" : "Schlüssel einfügen"} onChange={(ev) => setLicense(ev.target.value)} disabled={!admin} />
              <button className="btn" disabled={!license || !admin} onClick={() => { put({ license_key: license }, "Lizenzschlüssel"); setLicense(""); }}>Setzen</button>
              {p.license_set && admin && <button className="btn danger" onClick={() => put({ clear_license: true }, "Lizenzschlüssel")}>Entfernen</button>}
            </div>
          </Field>
          <div className="field">
            <span>Laufend</span>
            <div className="small">
              {e?.version ? `Version ${e.version}` : "–"}
              {e?.license_valid === true && " · Lizenz gültig"}
              {e?.license_valid === false && <span style={{ color: "var(--bad)" }}> · Lizenz ungültig{e.unlicensed_features ? ` (${e.unlicensed_features})` : ""}</span>}
              {e?.latency_ms ? ` · Latenz ${e.latency_ms} ms` : ""}
              {e?.preset ? ` · Preset ${e.preset}` : ""}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Presets</h2>
          <span className="spacer" />
          {admin && (
            <>
              <input ref={file} type="file" accept=".sts" style={{ display: "none" }} onChange={(ev) => { const f = ev.target.files?.[0]; if (f) upload(f); ev.target.value = ""; }} />
              <button className="btn" onClick={() => file.current?.click()}>⇪ Hochladen</button>
            </>
          )}
        </div>
        {!presets?.length ? <Empty>Noch keine Presets. Lade eine .sts-Datei hoch oder speichere eine aus der StereoTool-Oberfläche.</Empty> : (
          <table className="list">
            <tbody>
              {presets.map((x) => (
                <tr key={x.name}>
                  <td><b>{x.name}</b> {x.active && <span className="badge ok">aktiv</span>}</td>
                  <td className="small muted">{(x.size / 1024).toFixed(0)} KB · {fmtTime(x.modified)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {admin && !x.active && <button className="btn small primary" onClick={() => activate(x.name)}>Aktivieren</button>}{" "}
                    <a className="btn small" href={`/api/processor/presets/${encodeURIComponent(x.name)}`}>⇩ Herunterladen</a>{" "}
                    {admin && !x.active && <button className="btn small danger" onClick={() => remove(x.name)}>Löschen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>StereoTool-Bedienoberfläche</h2>
          <span className="spacer" />
          <a className="btn small" href={STEREOTOOL_URL} target="_blank" rel="noreferrer">In neuem Tab ↗</a>
          <button className="btn small" onClick={() => setShowWeb(!showWeb)}>{showWeb ? "Ausblenden" : "Einblenden"}</button>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>Die Original-Oberfläche von StereoTool, geschützt durch die Radiocast-Anmeldung (nur Verwaltung). Änderungen wirken sofort auf den Sendeweg.</p>
        {!p.enabled && <div className="banner warn">StereoTool ist aus – die Oberfläche ist erst erreichbar, wenn es läuft.</div>}
        {showWeb && <iframe className="stereotool" src={STEREOTOOL_URL} title="StereoTool" />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Stille

function Silence() {
  const { data, reload } = useLoad("/api/silence");
  const status = useStatus();
  const [v, setV] = useState<any>(null);
  useEffect(() => { if (data) setV(data); }, [data]);
  if (!v) return null;
  const dirty = JSON.stringify(v) !== JSON.stringify(data);
  const s = status?.engine?.silence;
  const save = async () => {
    try {
      saved(await api("/api/silence", { method: "PUT", body: v }), "Stilleerkennung");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="card">
      <h2>Stilleerkennung</h2>
      <p className="muted" style={{ marginTop: 0 }}>Bleibt der Sendeweg unter der Schwelle, meldet Radiocast das per Telegram. Läuft die Automation, springt sie zum nächsten Element; bleibt es still, kommt ein Titel aus dem Ausweichordner.</p>
      <div className="form">
        <Field label="Schwelle (dBFS)"><input type="number" min={-90} max={-10} value={v.threshold_db} onChange={(e) => setV({ ...v, threshold_db: Number(e.target.value) })} /></Field>
        <Field label="Dauer (Sekunden)"><input type="number" min={2} max={600} value={v.seconds} onChange={(e) => setV({ ...v, seconds: Number(e.target.value) })} /></Field>
        <Field label="Aktion" full><Switch checked={v.skip} onChange={(x) => setV({ ...v, skip: x })} label={v.skip ? "Überspringen, dann Ausweichtitel" : "Nur melden"} /></Field>
        <div className="field full">
          <span>Pegel jetzt {s ? `(${s.level_db.toFixed(0)} dBFS)` : ""}</span>
          <Meter db={s?.level_db} mark={v.threshold_db} />
          {s?.silent && <span className="badge bad" style={{ marginTop: 6 }}>STILLE</span>}
        </div>
      </div>
      <div className="savebar">
        <span className="small muted">{dirty ? "Ungespeicherte Änderungen" : "Alle Änderungen gespeichert"}</span>
        <span className="spacer" />
        <button className="btn" disabled={!dirty} onClick={() => setV(data)}>Verwerfen</button>
        <button className="btn primary" disabled={!dirty} onClick={save}>Speichern</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Sender

function StationSettings() {
  const { data, reload } = useLoad("/api/station");
  const [v, setV] = useState<any>(null);
  useEffect(() => { if (data) setV(data); }, [data]);
  if (!v) return null;
  const dirty = JSON.stringify(v) !== JSON.stringify(data);
  const save = async () => {
    try {
      saved(await api("/api/station", { method: "PUT", body: v }), "Sender");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="card">
      <h2>Sender</h2>
      <div className="form">
        <Field label="Name"><input type="text" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label="Slogan"><input type="text" value={v.slogan} onChange={(e) => setV({ ...v, slogan: e.target.value })} /></Field>
        <Field label="Zeitzone"><input type="text" value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })} /></Field>
        <Field label="Region"><input type="text" value={v.region} onChange={(e) => setV({ ...v, region: e.target.value })} /></Field>
        <Field label="Format"><input type="text" value={v.format} onChange={(e) => setV({ ...v, format: e.target.value })} placeholder="z. B. AC, CHR, Lokalradio" /></Field>
        <Field label="Lautheitsziel (LUFS)" hint="Titel mit gemessener Lautheit werden darauf angeglichen"><input type="number" step={0.5} min={-30} max={-6} value={v.loudness_target} onChange={(e) => setV({ ...v, loudness_target: Number(e.target.value) })} /></Field>
      </div>
      <div className="savebar">
        <span className="small muted">{dirty ? "Ungespeicherte Änderungen" : "Alle Änderungen gespeichert"}</span>
        <span className="spacer" />
        <button className="btn" disabled={!dirty} onClick={() => setV(data)}>Verwerfen</button>
        <button className="btn primary" disabled={!dirty} onClick={save}>Speichern</button>
      </div>
    </div>
  );
}

export function Settings({ tab = "ausgaenge" }: { tab?: string }) {
  let body;
  switch (tab) {
    case "metadaten": body = <Metadata />; break;
    case "monitor": body = <Monitors />; break;
    case "klang": body = <Sound />; break;
    case "stille": body = <Silence />; break;
    case "abspielen": body = <Playback />; break;
    case "sender": body = <StationSettings />; break;
    default: body = <Outputs />;
  }
  return (
    <div>
      <PageHead title="Einstellungen" />
      <div className="tabs">
        {TABS.map(([k, l]) => <a key={k} href={`#/einstellungen/${k}`} className={k === (tab || "ausgaenge") ? "active" : ""}>{l}</a>)}
      </div>
      <HowTo text="Alle Änderungen gehen per sanftem Neustart an die Engine – Hörer bleiben verbunden." chapter="einstellungen" />
      {body}
    </div>
  );
}


const WDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WORDER = [1, 2, 3, 4, 5, 6, 0];
const hm = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const tm = (s: string) => { const [h, m] = s.split(":").map(Number); return (h || 0) * 60 + (m || 0); };

/** Time windows of a stream: days + from–to (over midnight allowed), several per stream; none = always on. */
export function OutputWindows({ value, onChange }: { value: any[]; onChange: (w: any[]) => void }) {
  const upd = (i: number, p: any) => onChange(value.map((w, j) => (j === i ? { ...w, ...p } : w)));
  return (
    <div className="stack" style={{ gap: 6 }}>
      {!value.length && <span className="small muted">immer an</span>}
      {value.map((w, i) => (
        <div key={i} className="row wrap" style={{ gap: 4 }}>
          {WORDER.map((d) => (
            <label key={d} className="badge" style={{ cursor: "pointer", background: w.days.includes(d) ? "var(--accent-2)" : undefined }}>
              <input type="checkbox" checked={w.days.includes(d)} onChange={() => upd(i, { days: w.days.includes(d) ? w.days.filter((x: number) => x !== d) : [...w.days, d] })} /> {WDAYS[d]}
            </label>
          ))}
          <input type="time" value={hm(w.from)} onChange={(e) => upd(i, { from: tm(e.target.value) })} style={{ width: 100 }} />
          <span>–</span>
          <input type="time" value={w.to >= 1440 ? "00:00" : hm(w.to)} onChange={(e) => upd(i, { to: tm(e.target.value) || 1440 })} style={{ width: 100 }} />
          <button type="button" className="iconbtn" onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <div className="row" style={{ gap: 6 }}>
        <button type="button" className="btn small" onClick={() => onChange([...value, { days: [1, 2, 3, 4, 5], from: 360, to: 600 }])}>+ Zeitfenster</button>
        {value.length > 0 && <button type="button" className="btn small" onClick={() => onChange([])}>immer an</button>}
      </div>
    </div>
  );
}
